/*!
 * MatchEngine - compatibility scoring for bazi-calculator.org
 *
 * The arithmetic is a line-for-line port of the production scorer
 * (marriage_calculator.py, by way of the Chinese site's bazi-core.js), so this
 * page, the Chinese site and the paid Blueprint cannot disagree about a score.
 * Only the prose differs: descriptions are returned as keys plus data, and the
 * page turns them into whatever language it is running in.
 *
 * Depends on ChartEngine for the two charts. It reads them through an adapter
 * so the scoring code keeps the exact field names it was ported with.
 */
(function (root) {
  'use strict';

  var HE_GAN = {
    '甲己': 1, '己甲': 1, '乙庚': 1, '庚乙': 1, '丙辛': 1,
    '辛丙': 1, '丁壬': 1, '壬丁': 1, '戊癸': 1, '癸戊': 1
  };
  var LIU_HE = ['子丑','丑子','寅亥','亥寅','卯戌','戌卯','辰酉','酉辰','巳申','申巳','午未','未午'];
  var SAN_HE = [['申','子','辰'], ['寅','午','戌'], ['巳','酉','丑'], ['亥','卯','未']];
  var LIU_CHONG = ['子午','午子','丑未','未丑','寅申','申寅','卯酉','酉卯','辰戌','戌辰','巳亥','亥巳'];
  var LIU_HAI = ['子未','未子','丑午','午丑','寅巳','巳寅','卯辰','辰卯','申亥','亥申','酉戌','戌酉'];
  var GENERATE = { Wood:'Fire', Fire:'Earth', Earth:'Metal', Metal:'Water', Water:'Wood' };
  var RESTRAIN = { Wood:'Earth', Earth:'Water', Water:'Fire', Fire:'Metal', Metal:'Wood' };
  var ZH2EN = { '木':'Wood', '火':'Fire', '土':'Earth', '金':'Metal', '水':'Water' };
  var ANIMAL = {
    '子':'Rat','丑':'Ox','寅':'Tiger','卯':'Rabbit','辰':'Dragon','巳':'Snake',
    '午':'Horse','未':'Goat','申':'Monkey','酉':'Rooster','戌':'Dog','亥':'Pig'
  };
  function has(list, x) { return list.indexOf(x) >= 0; }

  /* ChartEngine speaks a different dialect to the scorer it was ported from.
     Translate once, here, rather than editing the scoring code. */
  function adapt(c) {
    var s = c.strength || {};
    /* Seasonally weighted, not the raw tally: the scorer was written against
       the weighted figures and a bare count silently changes every score. */
    var eff = s.effectiveElements || {};
    return {
      dayMaster: c.dayMaster,
      dayMasterElement: c.dayMasterElement,
      dayMasterStrength: s.verdict || 'balanced',
      favorableElements: s.favour || [],
      effectiveElements: eff,
      tiaohouNeeds: s.climateNeed || null,
      animal: ANIMAL[c.pillars.year.branch] || '',
      pillars: {
        year:  { gan: c.pillars.year.stem,  zhi: c.pillars.year.branch,  naYin: c.pillars.year.soundElement },
        month: { gan: c.pillars.month.stem, zhi: c.pillars.month.branch, naYin: c.pillars.month.soundElement },
        day:   { gan: c.pillars.day.stem,   zhi: c.pillars.day.branch,   naYin: c.pillars.day.soundElement },
        hour:  { gan: c.pillars.hour.stem,  zhi: c.pillars.hour.branch,  naYin: c.pillars.hour.soundElement }
      }
    };
  }

  function scoreDayMaster(a, b) {
    var s = 12, key = 'neutral', data = {};
    if (HE_GAN[a.dayMaster + b.dayMaster]) {
      s += 10; key = 'combine'; data = { a: a.dayMaster, b: b.dayMaster };
    } else if (a.dayMasterElement === b.dayMasterElement) {
      s += 5; key = 'same'; data = { el: a.dayMasterElement };
    } else if (GENERATE[a.dayMasterElement] === b.dayMasterElement) {
      s += 7; key = 'feeds'; data = { from: a.dayMasterElement, to: b.dayMasterElement };
    } else if (GENERATE[b.dayMasterElement] === a.dayMasterElement) {
      s += 7; key = 'feeds'; data = { from: b.dayMasterElement, to: a.dayMasterElement };
    } else if (RESTRAIN[a.dayMasterElement] === b.dayMasterElement ||
               RESTRAIN[b.dayMasterElement] === a.dayMasterElement) {
      s += 2; key = 'controls';
    }
    var extra = null;
    if ((a.dayMasterStrength === 'strong' && b.dayMasterStrength === 'weak') ||
        (a.dayMasterStrength === 'weak' && b.dayMasterStrength === 'strong')) {
      s += 3; extra = 'balance';
    }
    return { score: Math.min(25, Math.round(s)), max: 25, key: key, data: data, extra: extra };
  }

  function scoreZodiac(a, b) {
    var za = a.pillars.year.zhi, zb = b.pillars.year.zhi, pair = za + zb, s, key;
    if (has(LIU_HE, pair)) { s = 20; key = 'six_harmony'; }
    else if (SAN_HE.some(function (g) { return has(g, za) && has(g, zb); })) { s = 17; key = 'triad'; }
    else if (has(LIU_CHONG, pair)) { s = 8; key = 'clash'; }
    else if (has(LIU_HAI, pair)) { s = 9; key = 'harm'; }
    else { s = 13; key = 'plain'; }
    return { score: s, max: 20, key: key, data: { a: a.animal, b: b.animal } };
  }

  function scoreElements(a, b) {
    var s = 10, comp = 0, notes = [];
    a.favorableElements.forEach(function (el) {
      if ((b.effectiveElements[el] || 0) >= 3) { comp++; notes.push({ dir: 'theirs', el: el }); }
    });
    b.favorableElements.forEach(function (el) {
      if ((a.effectiveElements[el] || 0) >= 3) { comp++; notes.push({ dir: 'yours', el: el }); }
    });
    if (a.tiaohouNeeds && (b.effectiveElements[a.tiaohouNeeds] || 0) >= 2) comp++;
    if (b.tiaohouNeeds && (a.effectiveElements[b.tiaohouNeeds] || 0) >= 2) comp++;
    var conflict = 0;
    ['Wood','Fire','Earth','Metal','Water'].forEach(function (el) {
      if ((a.effectiveElements[el] || 0) <= 1 && (b.effectiveElements[el] || 0) <= 1) conflict++;
    });
    s += comp * 2.5 - conflict * 1.5;
    var key = comp >= 3 ? 'excellent' : comp >= 1 ? 'some' : 'neutral';
    return { score: Math.max(8, Math.min(20, Math.round(s))), max: 20,
             key: key, notes: notes, complements: comp, gaps: conflict };
  }

  function scoreNaYin(a, b) {
    var ea = ZH2EN[(a.pillars.year.naYin || '').slice(-1)];
    var eb = ZH2EN[(b.pillars.year.naYin || '').slice(-1)];
    var s, key;
    if (ea && ea === eb) { s = 12; key = 'same'; }
    else if (GENERATE[ea] === eb || GENERATE[eb] === ea) { s = 14; key = 'feeds'; }
    else if (RESTRAIN[ea] === eb || RESTRAIN[eb] === ea) { s = 8; key = 'controls'; }
    else { s = 10; key = 'apart'; }
    return { score: s, max: 15, key: key, data: { a: ea || '', b: eb || '' } };
  }

  function scoreGanZhi(a, b) {
    var s = 5, m = [];
    var slots = ['year','month','day','hour'];
    var gA = slots.map(function (k) { return a.pillars[k].gan; });
    var gB = slots.map(function (k) { return b.pillars[k].gan; });
    var zA = slots.map(function (k) { return a.pillars[k].zhi; });
    var zB = slots.map(function (k) { return b.pillars[k].zhi; });
    gA.forEach(function (x) { gB.forEach(function (y) {
      if (HE_GAN[x + y]) { s += 0.8; m.push(x + y); } }); });
    zA.forEach(function (x) { zB.forEach(function (y) {
      if (has(LIU_HE, x + y)) { s += 0.6; m.push(x + y); } }); });
    var key = m.length >= 4 ? 'many' : m.length >= 2 ? 'few' : m.length === 1 ? 'one' : 'none';
    return { score: Math.min(10, Math.round(s)), max: 10, key: key, matches: m, count: m.length };
  }

  function scoreSpouse(a, b) {
    var x = a.pillars.day.zhi, y = b.pillars.day.zhi, pair = x + y, s, key;
    if (has(LIU_HE, pair)) { s = 10; key = 'combine'; }
    else if (SAN_HE.some(function (g) { return has(g, x) && has(g, y); })) { s = 9; key = 'support'; }
    else if (has(LIU_CHONG, pair)) { s = 5; key = 'clash'; }
    else { s = 7; key = 'neutral'; }
    return { score: Math.max(5, Math.min(10, s)), max: 10, key: key };
  }

  /**
   * @param {object} chartA output of ChartEngine.build
   * @param {object} chartB output of ChartEngine.build
   */
  function score(chartA, chartB) {
    var a = adapt(chartA), b = adapt(chartB);
    var br = {
      dayMaster:    scoreDayMaster(a, b),
      zodiac:       scoreZodiac(a, b),
      elements:     scoreElements(a, b),
      naYin:        scoreNaYin(a, b),
      ganZhi:       scoreGanZhi(a, b),
      spousePalace: scoreSpouse(a, b)
    };
    var raw = 0;
    Object.keys(br).forEach(function (k) { raw += br[k].score; });
    /* The raw 40-100 band is stretched onto 60-99: a compatibility score of
       under 60 tells a reader nothing useful and reads as a verdict on the
       relationship, which is not what this measures. */
    var total = Math.round(60 + (raw - 40) * (39 / 60));
    total = Math.max(60, Math.min(99, total));
    var level = total >= 90 ? { key: 'made_for_each_other', stars: 5 }
              : total >= 80 ? { key: 'strong_match', stars: 4 }
              : total >= 70 ? { key: 'workable', stars: 3 }
              :               { key: 'needs_work', stars: 2 };
    return { total: total, raw: raw, breakdown: br, level: level, a: a, b: b };
  }

  root.MatchEngine = { score: score, adapt: adapt, ANIMAL: ANIMAL };
})(window);
